import { useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information-circle.svg';
import { ExternalLinkIcon } from '@/components/icons/external-link';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/utils/classnames';

import { useHiresToDate } from './context';
import { HiresToDateTimeline } from './hires-to-date-timeline';

const HELP_ARTICLE_URL =
  'https://support.fountain.com/en/articles/16786663-view-all-campaigns-in-source';

interface HiresToDateSwitchProps {
  /** Called with the new state when the user flips the switch. */
  onToggle: (next: boolean) => void;
  /** Applicants in the range hired after its end date, for the explainer. */
  hiresAfterRangeEnd: number;
}

/**
 * The Hires to date switch and its explainer, for the Sourced this period card
 * header. On a range that includes today it stays focusable and says why it
 * does nothing.
 */
export function HiresToDateSwitch({
  onToggle,
  hiresAfterRangeEnd,
}: HiresToDateSwitchProps) {
  const intl = useIntl();
  const switchId = useId();
  const { isEligible, isOn } = useHiresToDate();

  const ineligibleReason = intl.formatMessage({
    defaultMessage:
      'Hires to date works for date ranges that end before today.',
    description:
      'Tooltip on the disabled Hires to date switch on All Campaigns, explaining it only applies to a past date range',
  });

  // The negative margin keeps the 24px info button and the body-size label from
  // making this card's header taller than its neighbour's.
  return (
    <div className="-my-1 flex items-center gap-2">
      <Typography
        as="label"
        variant="body1"
        htmlFor={switchId}
        className={cn(
          'whitespace-nowrap',
          isEligible ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
        )}
      >
        <FormattedMessage
          defaultMessage="Hires to date"
          description="Label of the switch on All Campaigns that counts hires of a past range's applicants up to today"
        />
      </Typography>
      <Tooltip content={isEligible ? null : ineligibleReason}>
        <Switch
          id={switchId}
          checked={isOn}
          aria-disabled={!isEligible}
          onCheckedChange={next => {
            if (isEligible) onToggle(next);
          }}
          className={cn(!isEligible && 'cursor-not-allowed opacity-50')}
        />
      </Tooltip>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="size-6 rounded-sm p-0"
            aria-label={intl.formatMessage({
              defaultMessage: 'How hires to date are counted',
              description:
                'Accessible name of the info button beside the Hires to date switch on All Campaigns',
            })}
          >
            <img src={InfoIcon} alt="" className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="flex w-[340px] flex-col gap-3"
          aria-label={intl.formatMessage({
            defaultMessage: 'Hires to date',
            description:
              'Accessible name of the popover explaining how Hires to date is counted on All Campaigns',
          })}
        >
          <div className="flex flex-col gap-1">
            <Typography variant="body2">
              <FormattedMessage
                defaultMessage="Hires to date"
                description="Title of the popover explaining how Hires to date is counted on All Campaigns"
              />
            </Typography>
            <Typography variant="subtitle1">
              <FormattedMessage
                defaultMessage="Counts every hire so far from applicants sourced in the selected range, including people hired after the range ended."
                description="Body of the Hires to date popover on All Campaigns: what the switch counts"
              />
            </Typography>
          </div>
          <HiresToDateTimeline hiresAfterRangeEnd={hiresAfterRangeEnd} />
          <Typography variant="small1" className="text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Changes Hires, Conv., CPH and Conversion. Applicants and spend stay within the range."
              description="Hires to date popover on All Campaigns: which metrics the switch changes and which stay the same"
            />
          </Typography>
          <Typography
            as="a"
            variant="body2"
            href={HELP_ARTICLE_URL}
            target="_blank"
            rel="noreferrer"
            className="text-fx-primary inline-flex w-fit items-center gap-1"
          >
            <FormattedMessage
              defaultMessage="Read help article"
              description="Link at the bottom of the Hires to date popover on All Campaigns that opens the help article in a new tab"
            />
            <ExternalLinkIcon className="text-fx-primary size-4" />
          </Typography>
        </PopoverContent>
      </Popover>
    </div>
  );
}
